import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  MagnifyingGlass, Bank, Copy, PencilSimple, Check, X, Warning,
  CaretDown, CaretUp, Funnel
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BANCOS_CHILE = [
  'Banco de Chile', 'Banco Estado', 'Banco Santander', 'BCI', 'Banco Scotiabank',
  'Banco Itau', 'Banco BICE', 'Banco Falabella', 'Banco Ripley', 'Banco Consorcio',
  'Banco Security', 'HSBC', 'Banco Internacional', 'Coopeuch', 'MACH', 'Tenpo', 'Mercado Pago',
  'Tapp', 'CopecPay',
];
const TIPOS_CUENTA = ['Cuenta Corriente', 'Cuenta Vista', 'Cuenta de Ahorro', 'Cuenta RUT', 'Chequera Electronica'];
const STATUS_COLORS = { activo: 'bg-emerald-100 text-emerald-700', 'con licencia': 'bg-amber-100 text-amber-700', vacaciones: 'bg-blue-100 text-blue-700', desvinculado: 'bg-red-100 text-red-700', suspendido: 'bg-zinc-200 text-zinc-600' };

import { usePermissions } from '../hooks/usePermissions';

const BankAccountsPage = () => {
  const { axiosInstance, isAdmin, user, canManageBankAccounts } = useAuth();
  const { canManage } = usePermissions();
  const canEditBankAccounts = canManage('cuentas_bancarias');
  const [workers, setWorkers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('activo');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [bankForm, setBankForm] = useState({
    banco: '', tipo_cuenta: '', numero_cuenta: '', rut_titular: '', nombre_titular: '', email_titular: '',
  });
  const [rutError, setRutError] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterBranch) params.branch_id = filterBranch;
      if (search) params.search = search;
      const [workersRes, branchesRes] = await Promise.all([
        axiosInstance.get('/api/workers/bank/list', { params }),
        isAdmin ? axiosInstance.get('/api/branches') : Promise.resolve({ data: [] }),
      ]);
      let filtered = workersRes.data;
      if (filterStatus) {
        filtered = filtered.filter(w => w.status === filterStatus);
      }
      setWorkers(filtered);
      setBranches(branchesRes.data);
    } catch (e) {
      // error handled silently
    } finally { setLoading(false); }
  }, [axiosInstance, isAdmin, filterBranch, search, filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copiado`);
    }).catch(() => {
      toast.error('No se pudo copiar');
    });
  };

  const openEdit = (worker) => {
    setEditingWorker(worker);
    setBankForm({
      banco: worker.banco || '',
      tipo_cuenta: worker.tipo_cuenta || '',
      numero_cuenta: worker.numero_cuenta || '',
      rut_titular: worker.rut_titular || '',
      nombre_titular: worker.nombre_titular || '',
      email_titular: worker.email_titular || '',
    });
    setRutError('');
    setShowEditModal(true);
  };

  const validateRut = async (rut) => {
    if (!rut.trim()) { setRutError(''); return true; }
    try {
      const res = await axiosInstance.get(`/api/validate-rut/${encodeURIComponent(rut)}`);
      if (res.data.valid) {
        setBankForm(prev => ({ ...prev, rut_titular: res.data.normalized }));
        setRutError('');
        return true;
      } else {
        setRutError('RUT invalido. Verifique formato y digito verificador.');
        return false;
      }
    } catch {
      setRutError('Error al validar RUT');
      return false;
    }
  };

  const handleSave = async () => {
    if (bankForm.rut_titular) {
      const valid = await validateRut(bankForm.rut_titular);
      if (!valid) return;
    }
    setSaving(true);
    try {
      await axiosInstance.put(`/api/workers/${editingWorker.id}/bank`, bankForm);
      toast.success('Datos bancarios actualizados');
      setShowEditModal(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const getBranchName = (branchId) => {
    const b = branches.find(br => br.id === branchId);
    return b ? b.name : '';
  };

  const hasBank = (w) => w.banco || w.numero_cuenta || w.rut_titular;

  const workersWithBank = workers.filter(w => hasBank(w)).length;
  const workersWithoutBank = workers.length - workersWithBank;

  if (loading && workers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="bank-accounts-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="bank-page-title">
          Cuentas para Transferir
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Datos bancarios de trabajadores para transferencias de anticipos y pagos
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-zinc-200 bg-white p-3">
          <div className="text-2xl font-bold font-mono text-zinc-900">{workers.length}</div>
          <div className="text-xs text-zinc-500">Trabajadores</div>
        </div>
        <div className="border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-2xl font-bold font-mono text-emerald-700">{workersWithBank}</div>
          <div className="text-xs text-emerald-600">Con datos bancarios</div>
        </div>
        <div className={`border p-3 ${workersWithoutBank > 0 ? 'border-amber-200 bg-amber-50' : 'border-zinc-200 bg-white'}`}>
          <div className={`text-2xl font-bold font-mono ${workersWithoutBank > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>{workersWithoutBank}</div>
          <div className={`text-xs ${workersWithoutBank > 0 ? 'text-amber-600' : 'text-zinc-500'}`}>Sin datos bancarios</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, codigo o RUT..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input pl-9 w-full"
            data-testid="bank-search-input"
          />
        </div>
        {isAdmin && (
          <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}
            className="form-input w-auto" data-testid="bank-branch-filter">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="form-input w-auto" data-testid="bank-status-filter">
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="con licencia">Con Licencia</option>
          <option value="vacaciones">Vacaciones</option>
          <option value="desvinculado">Desvinculado</option>
        </select>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="bank-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Trabajador</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Cargo</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Banco</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Tipo Cuenta</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">N Cuenta</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">RUT Titular</th>
              <th className="text-left px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Nombre Titular</th>
              <th className="text-center px-4 py-2.5 text-xs uppercase tracking-wider font-medium text-zinc-500">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {workers.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center py-8 text-zinc-400">
                  {search ? 'No se encontraron resultados' : 'No hay trabajadores'}
                </td>
              </tr>
            )}
            {workers.map(w => {
              const hasBankData = hasBank(w);
              const isExpanded = expandedRow === w.id;
              return (
                <React.Fragment key={w.id}>
                  <tr className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${!hasBankData ? 'bg-amber-50/30' : ''}`}
                    data-testid={`bank-row-${w.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedRow(isExpanded ? null : w.id)} className="text-zinc-400 hover:text-zinc-600">
                          {isExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                        </button>
                        <div>
                          <span className="font-mono text-xs text-zinc-400 mr-2">{w.code}</span>
                          <span className="font-medium text-zinc-900">{w.first_name} {w.last_name}</span>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 font-medium ${STATUS_COLORS[w.status] || 'bg-zinc-100 text-zinc-500'}`}>
                          {w.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600">{w.cargo || '-'}</td>
                    <td className="px-4 py-2.5">
                      {w.banco ? (
                        <span className="text-zinc-900 font-medium">{w.banco}</span>
                      ) : (
                        <span className="text-zinc-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-600">{w.tipo_cuenta || <span className="text-zinc-300">-</span>}</td>
                    <td className="px-4 py-2.5">
                      {w.numero_cuenta ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-zinc-900">{w.numero_cuenta}</span>
                          <button onClick={() => copyToClipboard(w.numero_cuenta, 'N cuenta')}
                            className="text-zinc-400 hover:text-zinc-700 p-0.5" title="Copiar" data-testid={`copy-cuenta-${w.id}`}>
                            <Copy size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-zinc-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {w.rut_titular ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-zinc-900">{w.rut_titular}</span>
                          <button onClick={() => copyToClipboard(w.rut_titular, 'RUT')}
                            className="text-zinc-400 hover:text-zinc-700 p-0.5" title="Copiar" data-testid={`copy-rut-${w.id}`}>
                            <Copy size={14} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-zinc-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-zinc-700">
                      {w.nombre_titular || `${w.first_name} ${w.last_name}`}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {w.numero_cuenta && (
                          <button
                            onClick={() => {
                              const text = `${w.banco || ''}\n${w.tipo_cuenta || ''}\n${w.numero_cuenta || ''}\n${w.rut_titular || ''}\n${w.nombre_titular || w.first_name + ' ' + w.last_name}`;
                              copyToClipboard(text.trim(), 'Datos bancarios');
                            }}
                            className="text-xs px-2 py-1 text-emerald-600 hover:bg-emerald-50 border border-emerald-200 transition-colors flex items-center gap-1"
                            data-testid={`copy-all-${w.id}`}>
                            <Copy size={12} /> Copiar
                          </button>
                        )}
                        {canEditBankAccounts && (
                          <button onClick={() => openEdit(w)}
                            className="text-xs px-2 py-1 text-zinc-600 hover:bg-zinc-100 border border-zinc-200 transition-colors flex items-center gap-1"
                            data-testid={`edit-bank-${w.id}`}>
                            <PencilSimple size={12} /> Editar
                          </button>
                        )}
                        {!hasBankData && (
                          <Warning size={16} className="text-amber-500 ml-1" title="Sin datos bancarios" />
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <td colSpan="7" className="px-10 py-3">
                        <div className="grid grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-zinc-400 uppercase tracking-wider font-medium">Nombre titular</span>
                            <p className="text-zinc-900 mt-0.5">{w.nombre_titular || w.first_name + ' ' + w.last_name}</p>
                          </div>
                          <div>
                            <span className="text-zinc-400 uppercase tracking-wider font-medium">Email titular</span>
                            <p className="text-zinc-900 mt-0.5">{w.email_titular || '-'}</p>
                          </div>
                          <div>
                            <span className="text-zinc-400 uppercase tracking-wider font-medium">Ultima actualizacion</span>
                            <p className="text-zinc-900 mt-0.5">
                              {w.bank_updated_at ? new Date(w.bank_updated_at).toLocaleString('es-CL') : 'Sin actualizar'}
                            </p>
                          </div>
                          {isAdmin && getBranchName(w.branch_id) && (
                            <div>
                              <span className="text-zinc-400 uppercase tracking-wider font-medium">Sucursal</span>
                              <p className="text-zinc-900 mt-0.5">{getBranchName(w.branch_id)}</p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Bank size={20} className="text-emerald-600" />
              Datos Bancarios - {editingWorker?.code} {editingWorker?.first_name} {editingWorker?.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">Banco</label>
              <select value={bankForm.banco} onChange={(e) => setBankForm({ ...bankForm, banco: e.target.value })}
                className="form-input" data-testid="bank-form-banco">
                <option value="">Seleccionar banco...</option>
                {BANCOS_CHILE.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Tipo de Cuenta</label>
              <select value={bankForm.tipo_cuenta} onChange={(e) => setBankForm({ ...bankForm, tipo_cuenta: e.target.value })}
                className="form-input" data-testid="bank-form-tipo-cuenta">
                <option value="">Seleccionar tipo...</option>
                {TIPOS_CUENTA.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Numero de Cuenta</label>
              <input type="text" value={bankForm.numero_cuenta}
                onChange={(e) => setBankForm({ ...bankForm, numero_cuenta: e.target.value })}
                className="form-input font-mono" placeholder="Ej: 12345678"
                data-testid="bank-form-numero-cuenta" />
            </div>
            <div>
              <label className="form-label">RUT del Titular</label>
              <input type="text" value={bankForm.rut_titular}
                onChange={(e) => { setBankForm({ ...bankForm, rut_titular: e.target.value }); setRutError(''); }}
                onBlur={() => bankForm.rut_titular && validateRut(bankForm.rut_titular)}
                className={`form-input font-mono ${rutError ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder="12.345.678-5"
                data-testid="bank-form-rut" />
              {rutError && <p className="text-xs text-red-600 mt-1" data-testid="rut-error">{rutError}</p>}
            </div>
            <div>
              <label className="form-label">Nombre del Titular (opcional)</label>
              <input type="text" value={bankForm.nombre_titular}
                onChange={(e) => setBankForm({ ...bankForm, nombre_titular: e.target.value })}
                className="form-input" placeholder="Si es diferente al trabajador"
                data-testid="bank-form-nombre-titular" />
            </div>
            <div>
              <label className="form-label">Correo del Titular (opcional)</label>
              <input type="email" value={bankForm.email_titular}
                onChange={(e) => setBankForm({ ...bankForm, email_titular: e.target.value })}
                className="form-input" placeholder="correo@ejemplo.cl"
                data-testid="bank-form-email-titular" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !!rutError}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="save-bank-btn">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankAccountsPage;
